import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, GraduationCap, Briefcase, ChevronDown, ChevronRight, Maximize2 } from "lucide-react";
import { useState } from "react";
import InfoTip from "@/components/InfoTip";
import CandidateDetailModal from "@/components/CandidateDetailModal";

export default function CandidateCard({ result, selected, onToggle, index }) {
  const raw = result.candidate || {};
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // PRIVACY GUARD: Only safe, de-identified fields.
  const c = {
    anonymous_id: raw.anonymous_id,
    current_title: raw.current_title,
    current_employer_category: raw.current_employer_category,
    functional_area: raw.functional_area,
    therapeutic_areas: raw.therapeutic_areas,
    seniority_level: raw.seniority_level,
    highest_degree: raw.highest_degree,
    geography_region: raw.geography_region,
    ai_experience_summary: raw.ai_experience_summary,
  };
  const tas = (c.therapeutic_areas || []).filter(t => t && t !== "Unknown");

  const handleCardClick = (e) => {
    // Don't toggle if clicking the expand button or checkbox directly
    if (e.target.closest('[data-expand-btn]') || e.target.closest('[role="checkbox"]')) return;
    onToggle();
  };

  return (
    <Card
      data-testid={`candidate-card-${result.candidate_anonymous_id}`}
      onClick={handleCardClick}
      className={`rounded-xl border shadow-sm mb-3 transition-all duration-200 cursor-pointer ${
        selected
          ? "border-lime-500 bg-lime-50/40 ring-1 ring-lime-200"
          : "bg-card hover:border-slate-300 hover:shadow-md"
      }`}
      style={{ animationDelay: `${(index || 0) * 0.03}s` }}
    >
      <CardContent className="p-0">
        {/* Main Row — always visible */}
        <div className="flex items-center gap-4 p-4">
          {/* Checkbox */}
          <Checkbox
            data-testid={`select-candidate-${result.candidate_anonymous_id}`}
            checked={selected}
            onCheckedChange={onToggle}
            className="flex-shrink-0"
          />

          {/* Score circle */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 relative group ${
            result.composite_score >= 75 ? "bg-lime-100 text-lime-800" :
            result.composite_score >= 50 ? "bg-teal-50 text-teal-800" :
            "bg-slate-100 text-slate-600"
          }`} data-testid={`score-${result.candidate_anonymous_id}`}>
            <span className="text-base font-accent font-bold">{result.composite_score}</span>
            <div className="absolute -top-1 -right-1">
              <InfoTip text="Composite Score: 60% Experience Fit + 40% Intent Alignment. Scores above 75 indicate strong match on both credentials and career intent." side="right" />
            </div>
          </div>

          {/* Candidate info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h4 className="text-sm font-heading text-slate-900 truncate">{c.current_title || "Untitled"}</h4>
              <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">{result.candidate_anonymous_id}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
              {c.seniority_level && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-slate-400" />{c.seniority_level}</span>}
              {c.current_employer_category && <span>{c.current_employer_category}</span>}
              {c.geography_region && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{c.geography_region}</span>}
              {c.highest_degree && c.highest_degree !== "None" && c.highest_degree !== "null" && <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3 text-slate-400" />{c.highest_degree}</span>}
            </div>
          </div>

          {/* TA chips + actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {tas.slice(0, 2).map(ta => (
              <Badge key={ta} className="text-[10px] bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0">{ta}</Badge>
            ))}
            {tas.length > 2 && <span className="text-[10px] text-slate-400">+{tas.length - 2}</span>}
            <button
              data-expand-btn="true"
              data-testid={`detail-${result.candidate_anonymous_id}`}
              onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
              className="p-1.5 rounded hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
              title="View full profile"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              data-expand-btn="true"
              data-testid={`expand-${result.candidate_anonymous_id}`}
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className="p-1 rounded hover:bg-slate-100 transition-colors text-slate-400"
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Expanded detail — shown on click */}
        {expanded && (
          <div className="px-4 pb-4 pt-0 border-t border-slate-100 animate-slideDown">
            <div className="pt-3 grid grid-cols-2 gap-4">
              {/* Score breakdown */}
              <div>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                Score Breakdown
                <InfoTip text="Experience Fit: structured match on seniority, function, therapeutic area, and degree. Intent Alignment: AI-scored match between what your role offers and what the candidate's career signals suggest they want." side="right" />
              </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24">Experience Fit</span>
                    <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                      <div className="bg-evergreen-950 rounded-full h-1.5 transition-all" style={{ width: `${result.experience_fit_score}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-600 w-6 text-right">{result.experience_fit_score}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24">Intent Align</span>
                    <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                      <div className="bg-lime-600 rounded-full h-1.5 transition-all" style={{ width: `${result.intent_alignment_score}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-600 w-6 text-right">{result.intent_alignment_score}</span>
                  </div>
                </div>
              </div>

              {/* Candidate summary */}
              <div>
                {c.ai_experience_summary && (
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Profile Summary</p>
                    <p className="text-xs text-slate-500 leading-relaxed line-clamp-4">{c.ai_experience_summary}</p>
                  </div>
                )}
              </div>
            </div>

            {result.ai_match_rationale && (
              <div className="mt-3 p-2.5 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">AI Rationale:</span> {result.ai_match_rationale}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
      <CandidateDetailModal result={result} open={modalOpen} onClose={() => setModalOpen(false)} />
    </Card>
  );
}
